export function num(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return Math.round(value).toLocaleString("en-GB");
}

/** Short form for a headline figure, where the last three digits are noise. */
export function compact(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${trim(value / 1_000_000)}M`;
  if (abs >= 10_000) return `${trim(value / 1000)}k`;
  return Math.round(value).toLocaleString("en-GB");
}

function trim(value: number): string {
  return Number(value.toFixed(value < 10 ? 1 : 0)).toString();
}

/**
 * Money.
 *
 * `en-US` rather than `en-AU`, for one reason: an Australian locale renders USD
 * as "USD 12,345" and everything here is priced in USD, so every figure on the
 * site carried a three-letter prefix nobody needed. US English gives "$12,345"
 * for dollars and still marks anything else clearly (A$, EUR becomes €), so a
 * workspace that switches currency does not silently start lying about it.
 */
export function money(value: number | null | undefined, currency = "USD", digits = 0): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

export function pct(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return `${(value * 100).toFixed(digits)}%`;
}

export function signed(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  if (value === 0) return "0";
  return value > 0 ? `+${num(value)}` : `−${num(Math.abs(value))}`;
}

export function ago(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return "never";
  const seconds = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (seconds < 45) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 36) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 14) return `${days}d ago`;
  return `${Math.round(days / 7)}w ago`;
}

export function initialsOf(name: string): string {
  return name
    .split(/[\s._-]+/)
    .filter(Boolean)
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

/**
 * Categorical palette, brass-led so charts sit inside the theme rather than on
 * top of it. Shared by the donut, the bars and the legends so a colour means the
 * same thing wherever it appears.
 */
export const CATEGORY_COLOURS = [
  "#c7a768",
  "#6ca987",
  "#93a7c2",
  "#d47b79",
  "#9e5365",
  "#a98c5f",
  "#78909c",
  "#b9895f",
  "#7f9e8c",
  "#8d7fa5",
  "#c2a08c",
  "#5f7d8c",
];

export function colourFor(index: number): string {
  return CATEGORY_COLOURS[index % CATEGORY_COLOURS.length];
}

/**
 * A stable colour per dashboard name, so IG SUICIDE is the same shade in the
 * donut, the legend and the trend regardless of how it happened to rank today.
 */
const DASHBOARD_COLOURS: Record<string, string> = {
  "IG SUICIDE": "#c7a768",
  "IG MAIN": "#6ca987",
  TWITTER: "#93a7c2",
  REDDIT: "#d47b79",
  SFS: "#8d7fa5",
};

export function dashboardColour(name: string, fallbackIndex = 0): string {
  return DASHBOARD_COLOURS[name.toUpperCase()] ?? colourFor(fallbackIndex + 5);
}

/** Long strings, cut at the head where they are still distinguishable. */
export function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}
